import { useContext, useEffect, useState } from "react";
import {
  getProducts,
  createProduct,
  updateProduct,
  deleteProduct,
} from "../services/configurations/products";
import { Product, ProductProperty } from "../interfaces";
import { AppContext } from "../context";

type DataResponseType = "ALL" | "ID";
type ActionType = "CREATE" | "UPDATE" | "DELETE";

interface ProductWithProperties extends Product {
  productproperties?: ProductProperty[];
}

const productKeysToProperties = [
  "name",
  "nameinapi",
  "labelkey",
  "hastransactions",
  "fieldorder",
  "status",
  "releaseversion",
];

const handleActionFunction = (action: ActionType, t: Function) => {
  switch (action) {
    case "CREATE":
      return {
        actionFn: createProduct,
        fnMsg: t("Products.productApiCreateLabel"),
      };
    case "UPDATE":
      return {
        actionFn: updateProduct,
        fnMsg: t("Products.productApiUpdateLabel"),
      };
    case "DELETE":
      return {
        actionFn: deleteProduct,
        fnMsg: t("Products.productApiDeleteLabel"),
      };
    default:
      return {
        actionFn: () => {},
        fnMsg: "",
      };
  }
};

export const useProducts = (type: DataResponseType) => {
  const {
    t,
    toast,
    appState: { selectedApp, selectedProduct },
    setAppState,
  } = useContext(AppContext);
  const [data, setData] = useState<ProductWithProperties[] | null>(null);
  const [reload, setReload] = useState<number>(0);
  const appid = selectedApp?.appid || "0";

  const reloadFn = () => {
    setData(null);
    setReload((prev) => prev + 1);
  };

  const productAction = async (action: ActionType, data: any) => {
    const { actionFn, fnMsg } = handleActionFunction(action, t);
    setAppState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    const actionStatus = await Promise.resolve(actionFn(data, appid))
      .then((response) => {
        toast.current.replace({
          severity: "success",
          summary: "",
          detail: `${fnMsg}`,
          life: 3000,
        });
        return "SUCCESS";
      })
      .catch((error) => {
        toast.current.replace({
          severity: "error",
          summary: "",
          detail:
          error?.response?.data?.header?.errorMsg 
          ? error?.response?.data?.header?.errorMsg 
          : t("GlobalConfigurations.generalErrorMessage"),
          life: 3000,
        });
        return "ERROR";
      });
    setAppState((prev) => ({
      ...prev,
      isLoading: false,
    }));
    return actionStatus;
  };

  useEffect(() => {
    if (type === "ID" && appid) {
      (async function () {
        setAppState((prev) => ({
          ...prev,
          isLoading: true,
        }));
        await Promise.resolve(getProducts(appid, selectedProduct?.id))
          .then((response) => {
            const product: ProductWithProperties[] = [response?.data];
            const keyTypesOrder = ["text", "number", "boolean"];
            const extraProperties = productKeysToProperties.map((key) => {
              return {
                key,
                value:
                  key === "status"
                    ? `${(product[0] as any)[key] === "ACTIVE"}`
                    : `${(product[0] as any)[key]}`,
                type: ["hastransactions", "status"].includes(key)
                  ? "boolean"
                  : "text",
              };
            });
            product[0].productproperties
              ?.sort(function (a, b) {
                return (
                  keyTypesOrder.indexOf(a.type) - keyTypesOrder.indexOf(b.type)
                );
              })
              .unshift(...extraProperties);
            setData(product);
          })
          .catch((error) => {
            toast.current.replace({
              severity: "error",
              summary: "",
              detail: error?.response?.data?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
              life: 3000,
            });
          });
        setAppState((prev) => ({
          ...prev,
          isLoading: false,
        }));
      })();
    }
  }, [appid, toast, type, selectedProduct?.id, setAppState]);

  useEffect(() => {
    if (type === "ALL" && appid) {
      (async function () {
        await Promise.resolve(getProducts(appid))
          .then((response) => {
            const products: Product[] = response?.data;
            setData(products);
          })
          .catch((error) => {
            toast.current.replace({
              severity: "error",
              summary: "",
              detail: error?.response?.data?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
              life: 3000,
            });
          });
      })();
    }
  }, [appid, toast, reload, type]);

  return {
    data,
    productKeysToProperties,
    productAction,
    reloadFn,
  };
};
